import PageMeta from "../components/PageMeta";

/**
 * Privacy Policy
 *
 * Written for Google's OAuth verification reviewers. Contains the
 * EXACT Limited Use disclosure language Google looks for when granting
 * access to sensitive scopes (calendar). Do NOT paraphrase those
 * paragraphs without re-review.
 */
export default function Privacy() {
  return (
    <>
      <PageMeta
        title="Privacy Policy · TruckTrackr"
        description="How TruckTrackr collects, uses, stores, and deletes your personal data — including data accessed via Google APIs."
        canonical="https://thetrucktrackr.com/privacy"
      />
      <article
        className="mx-auto max-w-3xl px-5 py-10 legal-prose"
        data-testid="privacy-page"
      >
        <h1 className="font-display">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">
          Effective date: June 1, 2026 · Last updated: June 1, 2026
        </p>

        <p>
          TruckTrackr ("we", "us", or "our") operates{" "}
          <a href="https://thetrucktrackr.com">thetrucktrackr.com</a> (the
          "Service"), a directory and map for finding food trucks. This
          Privacy Policy explains what information we collect, how we use it,
          and the choices you have.
        </p>

        <h2>1. Information we collect</h2>
        <p>We collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Account information</strong> — when you sign up as a
            customer or food-truck owner: name, email address, password
            (hashed), and (for owners) your truck name.
          </li>
          <li>
            <strong>Profile information</strong> — for owners: business
            description, menu, photos, hours of operation, social-media
            links, and addresses you publish to your schedule.
          </li>
          <li>
            <strong>Location data</strong> — when you choose to share your
            device location, we use it transiently to sort nearby trucks. We
            do not store or share your customer location.
          </li>
          <li>
            <strong>Analytics</strong> — page views, button clicks, and
            session identifiers used to measure feature usage and improve
            the Service. Aggregated, not sold.
          </li>
          <li>
            <strong>Push notification tokens</strong> — if you opt in to
            push notifications, your browser/device push subscription is
            stored so we can deliver alerts you've requested.
          </li>
          <li>
            <strong>Google account data</strong> — see Section 4.
          </li>
        </ul>

        <h2>2. How we use information</h2>
        <ul>
          <li>To operate the Service and deliver the features you request.</li>
          <li>To send transactional emails (password reset, sync errors).</li>
          <li>
            To send opt-in marketing emails — you may opt out from any
            email or in your account settings.
          </li>
          <li>To improve the Service through aggregate analytics.</li>
          <li>
            To prevent fraud, abuse, and violations of our Terms of
            Service.
          </li>
        </ul>

        <h2>3. How we share information</h2>
        <p>
          We do <strong>not</strong> sell your personal information. We share
          information only:
        </p>
        <ul>
          <li>
            With service providers that operate the Service on our behalf
            (hosting, email delivery, payment processing), under
            confidentiality obligations.
          </li>
          <li>
            With law enforcement when required by law or to protect rights,
            property, or safety.
          </li>
          <li>
            In aggregate, non-identifiable form for analytics and research.
          </li>
        </ul>

        {/* === Google Limited Use Disclosure (verbatim language Google reviewers check for) === */}
        <h2 id="google-api-services">4. Google API Services User Data Policy</h2>
        <p>
          TruckTrackr's use and transfer of information received from Google
          APIs to any other app will adhere to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>

        <h3>4.1 What Google data we access</h3>
        <p>
          If you choose to connect your Google Calendar, we request the
          following OAuth scopes:
        </p>
        <ul>
          <li>
            <code>https://www.googleapis.com/auth/calendar</code> — to read
            events from the calendar you choose and to create / update / delete
            events in a dedicated "TruckTrackr Schedule" calendar inside
            your Google account.
          </li>
          <li>
            <code>https://www.googleapis.com/auth/userinfo.email</code> and{" "}
            <code>openid</code> — to display the email address of the
            connected Google account on your TruckTrackr dashboard so you
            can confirm the correct account is linked.
          </li>
        </ul>

        <h3>4.2 How we use Google Calendar data</h3>
        <p>
          We use the events we read from your chosen calendar for the
          single, user-visible purpose of populating your TruckTrackr
          schedule: an event's title becomes the truck appearance name,
          its location field becomes the published address (which we
          geocode to a map pin), and its start/end times become the
          appearance window. We never look at, store, or use events from
          calendars you have not explicitly selected.
        </p>

        <h3>4.3 Limited Use disclosure</h3>
        <p>
          TruckTrackr's use of information received from Google APIs
          adheres to the Google API Services User Data Policy, including
          the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
            target="_blank"
            rel="noopener noreferrer"
          >
            Limited Use requirements
          </a>
          . Specifically:
        </p>
        <ul>
          <li>
            We use Google user data only to provide or improve user-facing
            features that are prominent in the requesting application.
          </li>
          <li>
            We do <strong>not</strong> transfer Google user data to others
            except as necessary to provide or improve user-facing features
            that are prominent in the application's user interface, to
            comply with applicable law, or as part of a merger,
            acquisition, or sale of assets with notice to users.
          </li>
          <li>
            We do <strong>not</strong> use Google user data for serving
            advertisements, including retargeting, personalized, or
            interest-based advertising.
          </li>
          <li>
            We do <strong>not</strong> allow humans to read Google user
            data unless we have obtained your affirmative agreement to
            view specific messages, doing so is necessary for security
            purposes such as investigating abuse, to comply with applicable
            law, or for our internal operations and even then only when
            the data have been aggregated and anonymized.
          </li>
        </ul>

        <h3>4.4 Storage and retention of Google data</h3>
        <p>
          We store the following Google-derived data for as long as you
          keep the integration connected:
        </p>
        <ul>
          <li>Your Google account email (for identification on the dashboard).</li>
          <li>
            An OAuth access token and refresh token (encrypted at rest)
            so we can sync your calendar on a 5-minute schedule.
          </li>
          <li>
            Per-event metadata for the events we publish: Google event
            ID, title, location text, start/end timestamps, and a
            human-readable description if you provided one.
          </li>
        </ul>
        <p>
          When you disconnect the integration (see Section 5) or delete
          your TruckTrackr account, we revoke the OAuth token with Google
          and delete all stored Google-derived data within 30 days.
        </p>

        <h3>4.5 Facebook Page integration (optional, owners only)</h3>
        <p>
          Truck owners can optionally connect a Facebook Page they manage
          so TruckTrackr can publish their schedule updates to that Page
          and import schedules from the Page's posts. If you connect a
          Page, we access the following through Meta's Graph API:
        </p>
        <ul>
          <li>
            The list of Facebook Pages you manage (<code>pages_show_list</code>)
            — only so you can choose which Page to connect.
          </li>
          <li>
            Permission to publish posts to the connected Page on your
            behalf (<code>pages_manage_posts</code>) — used only when you
            publish a new stop with auto-posting enabled, or press "Send a
            test post".
          </li>
          <li>
            Permission to read the connected Page's own posts
            (<code>pages_read_engagement</code>) — used only when you press
            "Pull from Facebook" to import your schedule from recent posts.
          </li>
        </ul>
        <p>
          We store the connected Page's name and ID, and OAuth access
          tokens (encrypted at rest). We never post without an action you
          initiated or a setting you enabled, we never read other Pages or
          personal profiles, and we never sell or share Facebook data with
          third parties. When you disconnect the Page or delete your
          account, we delete all stored Facebook tokens and Page data
          within 30 days.
        </p>

        <h2 id="data-deletion">5. Your choices and how to disconnect / delete</h2>
        <ul>
          <li>
            <strong>Disconnect Google Calendar</strong> — open your
            TruckTrackr dashboard, scroll to the Google Calendar card, and
            click <em>Disconnect</em>. We revoke the token with Google and
            delete all stored calendar data within 30 days.
          </li>
          <li>
            <strong>Revoke from Google's side</strong> — at any time, visit{" "}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
            >
              your Google account permissions
            </a>{" "}
            and remove "TruckTrackr".
          </li>
          <li>
            <strong>Disconnect your Facebook Page</strong> — open your
            TruckTrackr dashboard, find the Facebook Page card, and click{" "}
            <em>disconnect</em>. We delete the stored access tokens and all
            Facebook-derived data within 30 days. You can also remove
            TruckTrackr from your Facebook settings under{" "}
            <em>Settings → Business integrations</em>.
          </li>
          <li>
            <strong>Delete your TruckTrackr account</strong> — email{" "}
            <a href="mailto:support@thetrucktrackr.com">
              support@thetrucktrackr.com
            </a>{" "}
            from the email address on file. We'll confirm and erase your
            account, profile, and all associated data (including any
            Google-derived data) within 30 days. See our{" "}
            <a href="/data-deletion">Data Deletion page</a> for the full
            procedure.
          </li>
          <li>
            <strong>Opt out of marketing emails</strong> — use the
            unsubscribe link in any marketing email, or your account
            settings.
          </li>
          <li>
            <strong>Disable push notifications</strong> — toggle off
            "Notify me when live" from any truck page, or revoke the
            permission in your browser/OS settings.
          </li>
        </ul>

        <h2>6. Security</h2>
        <p>
          We use industry-standard technical and organizational measures
          to protect your data: TLS in transit, encryption at rest for
          sensitive fields (including OAuth tokens), restricted internal
          access, and audited dependencies. No system is 100% secure; we
          will notify affected users without undue delay in the event of a
          security breach involving personal information.
        </p>

        <h2>7. Children's privacy</h2>
        <p>
          TruckTrackr is not intended for children under 13. We do not
          knowingly collect personal information from children under 13.
          If you believe a child has provided us with personal
          information, please contact us so we can delete it.
        </p>

        <h2>8. International users</h2>
        <p>
          TruckTrackr is operated from the United States. If you access
          the Service from outside the U.S., you consent to your data
          being transferred to and processed in the U.S.
        </p>

        <h2>9. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will
          post the new "Last updated" date above and, for material
          changes, notify you by email or in-app banner.
        </p>

        <h2>10. Contact</h2>
        <p>
          Questions or concerns? Email{" "}
          <a href="mailto:support@thetrucktrackr.com">
            support@thetrucktrackr.com
          </a>
          .
        </p>
      </article>
    </>
  );
}
